import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import {
  requestDetailsStepSchema,
  websiteChangeSchema,
  techSupportSchema,
  creativeDesignSchema,
  otherRequestSchema,
  newProjectSchema,
  type RequestDetailsStepData,
  type ContactStepData,
  type AIScopingResponse,
} from "@/lib/intake-schemas";
import { intakeStackedPrimaryCtaClassName } from "@/lib/intake-cta";
import { uploadIntakeAttachments } from "@/lib/intake-attachment-upload";
import {
  WORK_REQUEST_ATTACHMENT_MAX_FILES,
  type WorkRequestR2Attachment,
} from "@shared/work-request";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { CategorySelector } from "./category-selector";
import { FileUpload } from "./file-upload";
import { AIAnalysisOverlay } from "./ai-analysis-overlay";
import {
  createUnavailableAIScopingApiResponse,
  normalizeAIScopingResponse,
} from "@shared/intake-ai-scoping";
import {
  ArrowLeft,
  Video,
  Paperclip,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

interface RequestDetailsStepProps {
  contactData: ContactStepData;
  sessionId: string;
  onSubmit: (data: RequestDetailsStepData, aiScoping: AIScopingResponse) => void;
  onBack: () => void;
  initialData: RequestDetailsStepData | null;
}

export function RequestDetailsStep({
  contactData,
  sessionId,
  onSubmit,
  onBack,
  initialData,
}: RequestDetailsStepProps) {
  const [selectedCategory, setSelectedCategory] = useState<string>(
    initialData?.category || "",
  );
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<
    WorkRequestR2Attachment[]
  >(() =>
    (initialData?.attachments ?? []).filter(
      (attachment): attachment is WorkRequestR2Attachment =>
        Boolean(attachment.id && attachment.r2Key && attachment.contentType && attachment.sizeBytes),
    ),
  );
  const [showAttachments, setShowAttachments] = useState(
    () => (initialData?.attachments?.length ?? 0) > 0,
  );

  const getCategorySchema = () => {
    switch (selectedCategory) {
      case "website_change":
        return websiteChangeSchema;
      case "tech_support":
        return techSupportSchema;
      case "creative_design":
        return creativeDesignSchema;
      case "new_project":
        return newProjectSchema;
      case "other":
        return otherRequestSchema;
      default:
        return websiteChangeSchema;
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const form = useForm<any>({
    resolver: zodResolver(
      requestDetailsStepSchema.extend({
        requestDetails: getCategorySchema(),
      }),
    ),
    defaultValues: initialData || {
      category: "",
      requestDetails: {},
      loomUrl: "",
      attachments: [],
    },
  });

  const aiScopingMutation = useMutation({
    mutationFn: async (data: RequestDetailsStepData) => {
      const uploaded = await uploadIntakeAttachments(
        sessionId,
        uploadedFiles,
      );
      const attachments = [...existingAttachments, ...uploaded];

      try {
        const response = await apiRequest("POST", "/api/intake/ai-scoping", {
          category: data.category,
          requestDetails: data.requestDetails,
          loomUrl: data.loomUrl,
          contactData,
        });
        const aiScopingData = normalizeAIScopingResponse(
          await response.json(),
        ) as AIScopingResponse;
        return { aiScoping: aiScopingData, attachments };
      } catch (error) {
        const status = (error as { status?: number }).status;
        if (typeof status === "number" && status >= 400 && status < 500) {
          throw error;
        }
        return {
          aiScoping: createUnavailableAIScopingApiResponse(
            data.category,
          ) as AIScopingResponse,
          attachments,
        };
      }
    },
    onSuccess: ({ aiScoping, attachments }, variables) => {
      const dataWithAttachments = { ...variables, attachments };
      onSubmit(dataWithAttachments, aiScoping);
    },
  });

  const handleFormSubmit = (data: any) => {
    aiScopingMutation.mutate(data as RequestDetailsStepData);
  };

  return (
    <>
      {/* AI Analysis Overlay */}
      <AIAnalysisOverlay isActive={aiScopingMutation.isPending} />

      <div className="py-8">
        {/* Header */}
        <div className="mb-10 max-w-3xl">
          <p className="text-sm font-medium text-primary mb-2 tracking-wide uppercase">
            Request details
          </p>
          <h1 className="text-3xl sm:text-4xl font-bold mb-3 font-heading">
            Describe the work you need
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl">
            Give us enough context to understand the issue, confirm whether it
            sits within your current service, and prepare the right next step.
          </p>
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleFormSubmit)}
            className="space-y-10"
          >
            {/* ── Category Selection ── */}
            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem className="rounded-2xl border border-border bg-card p-6 shadow-sm">
                  <div className="mb-4">
                    <FormLabel className="text-base font-semibold">
                      What type of request is this?
                    </FormLabel>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Choose the closest fit so we can route your request to the
                      right team first time.
                    </p>
                  </div>
                  <FormControl>
                    <CategorySelector
                      value={field.value}
                      onChange={(value) => {
                        field.onChange(value);
                        setSelectedCategory(value);
                        form.setValue("requestDetails", {});
                      }}
                    />
                  </FormControl>
                  <FormMessage className="mt-4" />
                </FormItem>
              )}
            />

            {/* ── Category-specific fields with slide-in ── */}
            {selectedCategory && (
              <div className="animate-in slide-in-from-bottom-4 fade-in duration-300">
                {/* ─── Website Change ─── */}
                {selectedCategory === "website_change" && (
                  <div className="space-y-5 bg-card border border-border rounded-xl p-6">
                    <h3 className="text-lg font-semibold font-heading">
                      Website Change Details
                    </h3>

                    <FormField
                      control={form.control}
                      name="requestDetails.pageOrUrl"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Page or URL</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder="e.g., /about or https://example.com/page"
                              className="h-11"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="requestDetails.changeSummary"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>What needs to change? *</FormLabel>
                          <FormControl>
                            <Textarea
                              {...field}
                              placeholder="Describe the change in 1-2 sentences"
                              className="min-h-[100px]"
                            />
                          </FormControl>
                          <FormDescription>
                            Be as specific as possible about what needs to change
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="requestDetails.loginOrAccessNotes"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Login or Access Notes</FormLabel>
                          <FormControl>
                            <Textarea
                              {...field}
                              placeholder="Any login credentials or special access instructions"
                              className="min-h-[80px]"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                )}

                {/* ─── Tech Support ─── */}
                {selectedCategory === "tech_support" && (
                  <div className="space-y-5 bg-card border border-border rounded-xl p-6">
                    <h3 className="text-lg font-semibold font-heading">
                      Tech Support Details
                    </h3>

                    <FormField
                      control={form.control}
                      name="requestDetails.issueSummary"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>What's going wrong? *</FormLabel>
                          <FormControl>
                            <Textarea
                              {...field}
                              placeholder="Describe the technical issue you're experiencing"
                              className="min-h-[100px]"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="requestDetails.impact"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>How bad is it? *</FormLabel>
                          <FormControl>
                            <RadioGroup
                              onValueChange={field.onChange}
                              value={field.value}
                              className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1"
                            >
                              {[
                                { value: "site_down", label: "Site Down", desc: "Critical outage" },
                                { value: "broken_feature", label: "Broken Feature", desc: "Functionality not working" },
                                { value: "cosmetic", label: "Cosmetic", desc: "Visual issue only" },
                                { value: "other", label: "Other", desc: "Not sure / something else" },
                              ].map((opt) => (
                                <label
                                  key={opt.value}
                                  htmlFor={`impact_${opt.value}`}
                                  className={`
                                    flex items-center gap-3 p-3.5 rounded-lg border-2 cursor-pointer transition-all
                                    ${field.value === opt.value
                                      ? "border-primary bg-primary/5"
                                      : "border-border hover:border-muted-foreground/30"
                                    }
                                  `}
                                >
                                  <RadioGroupItem value={opt.value} id={`impact_${opt.value}`} />
                                  <div>
                                    <p className="text-sm font-medium">{opt.label}</p>
                                    <p className="text-xs text-muted-foreground">{opt.desc}</p>
                                  </div>
                                </label>
                              ))}
                            </RadioGroup>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                )}

                {/* ─── Creative / Design ─── */}
                {selectedCategory === "creative_design" && (
                  <div className="space-y-5 bg-card border border-border rounded-xl p-6">
                    <h3 className="text-lg font-semibold font-heading">
                      Creative / Design Details
                    </h3>

                    <FormField
                      control={form.control}
                      name="requestDetails.assetType"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>What do you need? *</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder='e.g., "Logo export", "A5 flyer", "Social media graphics"'
                              className="h-11"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="requestDetails.usageDeadline"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>When do you need it by?</FormLabel>
                          <FormControl>
                            <Input {...field} type="date" className="h-11" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="requestDetails.contentOrCopyAvailable"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Is your content / copy ready? *</FormLabel>
                          <FormControl>
                            <RadioGroup
                              onValueChange={field.onChange}
                              value={field.value}
                              className="grid grid-cols-2 gap-3 pt-1"
                            >
                              {[
                                { value: "yes", label: "Yes, it's ready" },
                                { value: "no", label: "No, I need help" },
                              ].map((opt) => (
                                <label
                                  key={opt.value}
                                  htmlFor={`copy_${opt.value}`}
                                  className={`
                                    flex items-center gap-3 p-3.5 rounded-lg border-2 cursor-pointer transition-all text-center justify-center
                                    ${field.value === opt.value
                                      ? "border-primary bg-primary/5"
                                      : "border-border hover:border-muted-foreground/30"
                                    }
                                  `}
                                >
                                  <RadioGroupItem value={opt.value} id={`copy_${opt.value}`} className="sr-only" />
                                  <span className="text-sm font-medium">{opt.label}</span>
                                </label>
                              ))}
                            </RadioGroup>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                )}

                {/* ─── New Project ─── */}
                {selectedCategory === "new_project" && (
                  <div className="space-y-5 bg-card border border-border rounded-xl p-6">
                    <div>
                      <h3 className="text-lg font-semibold font-heading">
                        New Project Details
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        Tell us about the project so we can shape a reliable estimate.
                      </p>
                    </div>

                    <FormField
                      control={form.control}
                      name="requestDetails.projectSummary"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>What are we building? *</FormLabel>
                          <FormControl>
                            <Textarea
                              {...field}
                              placeholder="Describe the project in a few sentences"
                              className="min-h-[120px]"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="requestDetails.goals"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Primary goals *</FormLabel>
                          <FormControl>
                            <Textarea
                              {...field}
                              placeholder="e.g., increase signups, refresh brand, launch new product"
                              className="min-h-[100px]"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="requestDetails.keyDeliverables"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Key deliverables</FormLabel>
                          <FormControl>
                            <Textarea
                              {...field}
                              placeholder="Pages, features, assets, campaigns, etc."
                              className="min-h-[80px]"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid gap-5 md:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="requestDetails.budgetRange"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Budget range *</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select budget" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="under_5k">Under £5k</SelectItem>
                                <SelectItem value="5k_15k">£5k – £15k</SelectItem>
                                <SelectItem value="15k_30k">£15k – £30k</SelectItem>
                                <SelectItem value="30k_plus">£30k+</SelectItem>
                                <SelectItem value="unsure">Not sure yet</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="requestDetails.timeline"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Timeline *</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select timeline" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="asap">ASAP</SelectItem>
                                <SelectItem value="one_month">1 month</SelectItem>
                                <SelectItem value="two_three_months">2 – 3 months</SelectItem>
                                <SelectItem value="flexible">Flexible</SelectItem>
                                <SelectItem value="unsure">Not sure yet</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="requestDetails.targetAudience"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Target audience</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder="Who is this for?"
                              className="h-11"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="requestDetails.successMeasures"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>How will success be measured?</FormLabel>
                          <FormControl>
                            <Textarea
                              {...field}
                              placeholder="KPIs, outcomes, or qualitative signals"
                              className="min-h-[80px]"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="requestDetails.stakeholders"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Stakeholders / decision makers</FormLabel>
                          <FormControl>
                            <Textarea
                              {...field}
                              placeholder="Names/roles and who signs off"
                              className="min-h-[80px]"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                )}

                {/* ─── Other ─── */}
                {selectedCategory === "other" && (
                  <div className="space-y-5 bg-card border border-border rounded-xl p-6">
                    <h3 className="text-lg font-semibold font-heading">
                      Tell Us More
                    </h3>

                    <FormField
                      control={form.control}
                      name="requestDetails.shortDescription"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>What do you need? *</FormLabel>
                          <FormControl>
                            <Textarea
                              {...field}
                              placeholder="Please provide a brief description of what you need"
                              className="min-h-[120px]"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                )}

                {/* ── Loom-First Section ── */}
                <div className="mt-8 grid gap-4 lg:grid-cols-[minmax(0,1.25fr)_minmax(0,0.75fr)]">
                  <div className="rounded-xl border border-border bg-card p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                        <Video className="w-6 h-6 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-base mb-1">
                          Optional video walkthrough
                        </h4>
                        <p className="text-sm text-muted-foreground mb-4">
                          A short Loom can help us understand the issue faster,
                          especially for bugs, content updates, or design
                          feedback.
                        </p>
                        <FormField
                          control={form.control}
                          name="loomUrl"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input
                                  {...field}
                                  type="url"
                                  placeholder="https://www.loom.com/share/..."
                                  className="h-11"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="rounded-xl border border-border bg-card p-6">
                    <button
                      type="button"
                      onClick={() => setShowAttachments(!showAttachments)}
                      className="flex w-full items-center gap-2 text-left text-sm font-medium text-foreground"
                    >
                      <Paperclip className="w-4 h-4 text-muted-foreground" />
                      Supporting files
                      {existingAttachments.length + uploadedFiles.length > 0 && (
                        <span className="ml-auto rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
                          {existingAttachments.length + uploadedFiles.length}
                        </span>
                      )}
                      {showAttachments ? (
                        <ChevronUp className="w-4 h-4 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-muted-foreground" />
                      )}
                    </button>
                    <p className="mt-2 text-sm text-muted-foreground">
                      Add screenshots, PDFs, videos, or briefs if they help
                      explain the request.
                    </p>

                    {showAttachments && (
                      <div className="mt-4 animate-in slide-in-from-top-2 fade-in duration-200">
                        <FileUpload
                          value={uploadedFiles}
                          onChange={setUploadedFiles}
                          existing={existingAttachments.map((attachment) => ({
                            id: attachment.id,
                            filename: attachment.filename,
                            sizeBytes: attachment.sizeBytes,
                          }))}
                          onRemoveExisting={(id) =>
                            setExistingAttachments((current) =>
                              current.filter((attachment) => attachment.id !== id),
                            )
                          }
                          maxFiles={WORK_REQUEST_ATTACHMENT_MAX_FILES}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ── Navigation ── */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button
                type="button"
                variant="ghost"
                onClick={onBack}
                className="order-2 sm:order-1 gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </Button>
              <Button
                type="submit"
                size="lg"
                disabled={aiScopingMutation.isPending || !selectedCategory}
                className={`${intakeStackedPrimaryCtaClassName} order-1 sm:order-2 sm:flex-initial sm:min-w-[220px]`}
              >
                Review request summary
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </>
  );
}
